import React from 'react';
import { User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * User menu — the signed-in identity dropdown shown at the top-right of
 * every authenticated screen.
 *
 * This is currently the ONLY shared header element in the app: there is
 * no layout component and no router, so each page renders its own
 * <header> and mounts <UserMenu navigateTo={navigateTo} /> itself —
 * `navigateTo` must be threaded down from App.js's view switcher.
 *
 * Contents: a non-interactive header (avatar initials, name, email,
 * staff role badges), then Your Profile and Settings (the 'profile' and
 * 'settings' views in App.js), any page-specific `extraItems`
 * ({ id, label, icon, onClick }), a divider, and Sign out.
 *
 * Built on @radix-ui/react-dropdown-menu (components/ui/dropdown-menu.js),
 * which replaced the previous hand-rolled useState + document
 * mousedown/Escape listeners. Radix handles outside-click and Escape
 * dismissal, and additionally provides arrow-key navigation, typeahead,
 * roving focus and focus return to the trigger on close — none of which
 * the hand-rolled version had. The identity header is a
 * DropdownMenuLabel so it is not keyboard-focusable.
 */
const UserMenu = ({ navigateTo, extraItems = [] }) => {
  const { user, logout } = useAuth();

  // Display name: staff users carry `name` from the Azure AD claims. The
  // first_name/last_name and email fallbacks are retained defensively —
  // a staff record seeded before its owner first signed in has no `name`.
  const displayName =
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    user?.email ||
    'Account';

  const initials = (() => {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    const src = parts[0] || (user?.email || '?');
    return src.slice(0, 2).toUpperCase();
  })();

  const handleSignOut = () => {
    logout();
    navigateTo('home');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center space-x-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-100 transition-colors group"
        >
          <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">
            {initials}
          </span>
          <span className="text-sm font-medium text-gray-700 max-w-[10rem] truncate hidden sm:inline">
            {displayName}
          </span>
          <ChevronDown
            className="h-4 w-4 text-gray-400 transition-transform group-data-[state=open]:rotate-180"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {/* Identity header — display only; a Label, not an Item, so it is
            skipped by keyboard navigation. */}
        <DropdownMenuLabel className="border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email || '—'}</p>
          {user?.isStaff && Array.isArray(user?.roles) && user.roles.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {user.roles.map((role) => (
                <span
                  key={role}
                  className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium"
                >
                  {role}
                </span>
              ))}
            </div>
          )}
        </DropdownMenuLabel>

        <DropdownMenuItem onSelect={() => navigateTo('profile')}>
          <User className="h-4 w-4 text-gray-400" />
          <span>Your Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigateTo('settings')}>
          <Settings className="h-4 w-4 text-gray-400" />
          <span>Settings</span>
        </DropdownMenuItem>

        {extraItems.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={item.id}
              // onSelect rather than onClick is the Radix menu callback.
              // extraItems can open a dialog (StaffPortal's permissions
              // matrix — hand-rolled, not Radix, so there is no focus
              // trap to fight and no deferral is needed). Note: do NOT
              // preventDefault() here — in Radix that keeps the menu open.
              onSelect={() => item.onClick()}
            >
              {Icon && <Icon className="h-4 w-4 text-gray-400" />}
              <span>{item.label}</span>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleSignOut}
          className="text-red-600 data-[highlighted]:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
